import { getDefaultClient } from '@dynamic-labs-sdk/client';
import { assertDefined } from '@dynamic-labs-sdk/client/core';
import type { WalletConnectConnectionResult } from '@dynamic-labs-sdk/wallet-connect';
import {
  getSignClient,
  importWalletConnectSession,
} from '@dynamic-labs-sdk/wallet-connect/core';

import { evmApiChainName, evmMethods } from '../consts';
import { createWalletProviderFromEIP155Session } from '../createWalletProviderFromEIP155Session';

type ConnectWithWalletConnectEvmParams = {
  /**
   * Whether this session's wallet accounts should be added to the dynamic's wallet accounts.
   */
  addToDynamicWalletAccounts?: boolean;
};

/** @not-instrumented */
export const connectWithWalletConnectEvm = async (
  { addToDynamicWalletAccounts = true }: ConnectWithWalletConnectEvmParams = {},
  client = getDefaultClient()
): Promise<WalletConnectConnectionResult> => {
  const signClient = await getSignClient(client);

  assertDefined(client.projectSettings, 'Project settings unavailable');

  const evmNetworks = client.projectSettings.networks?.find(
    (network) => network.chainName === evmApiChainName
  )?.networks;

  assertDefined(evmNetworks, 'EVM networks not found');

  const walletConnectChains = evmNetworks.map(
    (network) => `eip155:${network.networkId}`
  );

  /**
   * A WalletConnect connection request generates a URI and a promise.
   *
   * The URI must be handed to an external wallet application that will use it
   * to establish a connection via a WalletConnect relay server. Typically, this
   * is done by scanning a QR code or deep linking to the wallet application.
   *
   * The promise will resolve when the connection is established.
   */
  const { approval, uri } = await signClient.connect({
    optionalNamespaces: {
      eip155: {
        chains: walletConnectChains,
        events: ['accountsChanged', 'chainChanged', 'disconnected'],
        methods: evmMethods as unknown as string[],
      },
    },
  });

  assertDefined(
    uri,
    'Failed to establish WalletConnect session: no URI returned'
  );

  /**
   * Once the approval promise resolves, the WalletConnect session is established
   * between the wallet application and the dapp (this app).
   */
  const walletAccountsPromise = approval().then(async (session) => {
    assertDefined(
      session.namespaces.eip155,
      `WalletConnect session did not establish an EIP155 connection. Found instead: ${Object.keys(
        session.namespaces
      ).join(', ')}`
    );

    return importWalletConnectSession(
      {
        addToDynamicWalletAccounts,
        sessionTopic: session.topic,
        walletProviderBuilder: createWalletProviderFromEIP155Session,
      },
      client
    );
  });

  return { approval: () => walletAccountsPromise, uri };
};
